import { IUser } from '@/interfaces/user.interface';
import type { Permission } from './permission';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { getToken } from '@/utility/helper';
import { API_ENDPOINT, API_URL_USER_SERVICE } from '@/api/constant';
import { callApi } from '@/api/callApi';
import { setUserData } from '@/redux/features/UserSlice';
interface AuthContextType {
  userDetail: IUser | null;
  hasPermission: (permission: Permission) => boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  userDetail: null,
  hasPermission: () => false,
  isLoading: true,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const userDetail = useAppSelector((state) => state.user.user);
  const dispatch = useAppDispatch();
  useEffect(() => {
    const initUser = async () => {
      const token = getToken();
      if (!token) {
        dispatch(setUserData(null));
        setIsLoading(false);
        return;
      }
      if (userDetail) {
        setIsLoading(false);
        return;
      }
      try {
        const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.users}/detail`;
        const userData = await callApi<IUser>(PATH, { method: 'GET' });
        dispatch(setUserData(userData));
      } catch (error) {
        // console.error('Failed to fetch user:', error);
        dispatch(setUserData(null));
      } finally {
        setIsLoading(false);
      }
    };
    initUser();
  }, [userDetail, dispatch]);

  const hasPermission = (permission: Permission): boolean => {
    if (!userDetail || !userDetail.role?.permissions) return false;
    const [action, resource] = permission.split(':');
    const resourcePermission = userDetail.role.permissions.find(
      (p) => p.resource === resource,
    );
    return resourcePermission
      ? resourcePermission.actions.includes(action)
      : false;
  };

  return (
    <AuthContext.Provider value={{ userDetail, hasPermission, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
